import './forPrinting.css'
import Logo from '../../../../../assets/Logo/logosidetext.png'
import { forwardRef, useContext } from 'react'
import { Flex, Typography, ConfigProvider, Table, Space, Card, Row } from 'antd';
import { BridgerMPEAEL } from './Bridger/BridgerMPEAEL';
import { BridgerDiffuse } from './Bridger/BridgerDiffues';
import { BridgerANSI } from './Bridger/BridgerANSI';
import { EnvironmentContext } from '../../../../../routes/EnvironmentContext';
import { BridgerDiffuseANSI } from './Bridger/BridgerDiffuseANSI';

const { Text, Title, Paragraph } = Typography
const dateNow = new Date().toLocaleDateString('en-US')

// #region Declaration of all JSDocs used by Printable.jsx Component
/**
 * @typedef {Object} MPEData
 * @property {string} mpe - Maximum Permissible Exposure (e.g., "12.7 J/sqm")
 * @property {string} ae_applicable - Applicable Accessible Emission (e.g., "33.3 J/sqm")
 * @property {string} ae_actual - Actual Accessible Emission (e.g., "12.5 J/sqm")
 * @property {string} excess - Ratio of AE to MPE (e.g., "1.33")
 */

/**
 * @typedef {Object} LaserClass
 * @property {string} name - Class name (e.g., "Class 1")
 * @property {string} [ael] - Accessible Emission Limit (e.g., "33mJ")
 * @property {string} [excess] - AE/MPE ratio
 * @property {string} [pf] - Pass/Fail result
 * @property {boolean} [mostRestrictive] - Is this the most restrictive class?
 */

/**
 * @typedef {Object} MiscData
 * @property {string} name - Parameter name (e.g., "Aperture Stop")
 * @property {string} value - Parameter value (e.g., "100 mm")
 */

/**
 * @typedef {Object} AELCondition
 * @property {LaserClass[]} classes
 * @property {MiscData[]} misc
 */

/**
 * @typedef {Object} EyewearResult
 * @property {string} config - Configuration label (e.g., "Results at 200m")
 * @property {string} nominal_od - Nominal Optical Density (e.g., "0.9")
 * @property {string} rb_number - Recommended RB number
 * @property {string} lb_glass - Glass-based labeling
 * @property {string} lb_plastic - Plastic-based labeling
 */

/**
 * @typedef {Object} ResultData
 * @property {string} laserOperation
 * @property {string} sourceType
 * @property {string} standard
 * @property {string} wavelength
 * @property {string} time
 * @property {string} power
 * @property {string} beamDiameter
 * @property {string} beamDivergence
 * @property {string} distance
 * @property {string} spot_size
 * @property {string} eye_LA
 * @property {string} skin_LA
 * @property {string} applicable_eye
 * @property {string} applicable_skin
 * @property {string} timebase
 * @property {{ eye: MPEData, skin: MPEData }} mpe
 * @property {string} nohd
 * @property {string} enohd
 * @property {{ condition1: AELCondition, condition3: AELCondition }} ael
 * @property {EyewearResult[]} eyewear
 * @property {string} verdict
 */
//#endregion

const mpeObjectIsNullOrUndefined = (thisObject) => {
    const { mpe, ae_applicable, ae_actual, excess } = thisObject
    const stringArray = [mpe, ae_applicable, ae_actual, excess]
    return stringArray.some(x => !x)
}

/**
 * 
 * @param {ResultData} result 
 */
const validateResultInput = (result) => {
    if (!result) throw new Error(`Result Object is empty!`)
    const {
        laserOperation, sourceType, wavelength,
        time, power, beamDiameter, verdict,
        beamDivergence, distance, spot_size,
        eye_LA, skin_LA, applicable_eye, nohd, enohd
    } = result

    const strings = [
        laserOperation, sourceType, wavelength,
        time, power, beamDiameter, verdict,
        beamDivergence, distance, spot_size,
        eye_LA, skin_LA, applicable_eye, nohd, enohd
    ]

    if (strings.some(param => !param)) throw new Error('One or more strings are null or undefined.')
    // Next check the mpe object
    const expectedKeys = ['mpe', 'ae_applicable', 'ae_actual', 'excess']
    const { mpe } = result
    if (!mpe) throw new Error('MPE Object is null or undefined!')
    const { eye, skin } = mpe
    if (!eye || !skin) throw new Error('Eye or Skin object is null or undefined!')
    if (mpeObjectIsNullOrUndefined(eye)) throw new Error('Eye Object is missing one or more required strings.')
    if (mpeObjectIsNullOrUndefined(skin)) throw new Error('Skin Object is missing one or more required strings.')

    // Next, check the AE Object
    const { ael } = result
    if (!ael) throw new Error('AEL Object is null or undefined.')
    const { condition1, condition3 } = ael
    if (!condition1 || !condition3) throw new Error('Condition 1 or Condition 3 Object may be null or undefined.')
    const validateConditions = (conditionObject) => {
        const { classes, misc } = conditionObject
        if (!classes || !misc || classes?.length === 0 || misc?.length === 0) throw new Error('Classes or Misc Array is null, undefined or empty.')
        const isClassesValidated = classes.some(x => !x)
        const isMiscValidated = misc.some(x => !x)
        if (!isClassesValidated) throw new Error('One or more keys on Classes Array is null or undefined.')
        if (!isMiscValidated) throw new Error(`One or more keys on Misc Array is null or undefined.`)
        return true
    }
    validateConditions(condition1)
    validateConditions(condition3)

    // const aelConditionIsNullOrUndefined = (thisAELCondition) => {
    //     const { classes, misc } = thisAELCondition
    //     if (!classes || !misc || classes?.length === 0 || misc?.length === 0) throw new Error('Classes or Misc array is null or undefined.')
    //     const classesObjectContainsFalsey = classes.some((thisClassObject) => {
    //         const { name, ael, excess, pf } = thisClassObject
    //         return (!name || !ael || !excess || !pf)
    //     })
    //     if (classesObjectContainsFalsey) throw new Error('name, ael, excess or pf for one or more classes are null or undefined!')
    //     return false
    // }
    // aelConditionIsNullOrUndefined(condition1)
    // aelConditionIsNullOrUndefined(condition3)

    // Finally, check eyewear.
    const { eyewear } = result
    if (!eyewear) throw new Error('Eyewear Array is null or undefined')
    if (eyewear?.length === 0) throw new Error('Eyewear Array is present but is empty.')
    const hasInvalidEyewear = eyewear.some((eyeWearObject) => {
        const { config, nominal_od, rb_number, lb_glass, lb_plastic } = eyeWearObject
        return [config, nominal_od, rb_number, lb_glass, lb_plastic].some(x => !x)
    })
    if (hasInvalidEyewear) throw new Error('One or more eyewear object keys are null or undefined!')
    return true
}


const Printable = forwardRef((props, ref) => {
    let { selectedStandard } = useContext(EnvironmentContext)
  
    selectedStandard = selectedStandard === 'iec' ? 'IEC 60825-1' : 'ANSI Z136.1'
    // console.log('printable', props, selectedStandard)
    const x = (() => {

        if (selectedStandard.includes('ANSI')) {
            if (props.result.laserOperation.includes('diffuse')) {
                return BridgerDiffuseANSI(props, selectedStandard)
            }
            else {
                return BridgerANSI(props, selectedStandard)
            }
        }
        else {
            if (props.result.laserOperation.includes('diffuse')) {
                return BridgerDiffuse(props, selectedStandard)
            } else {
                return BridgerMPEAEL(props, selectedStandard)
            }
        }
    })()
    // console.log("x", x)

    // validateResultInput(x)

    //#region All the abstractions used in this Component
    const customTheme = {
        token: {
            colorPrimary: "#fffff"
        }
    }
    const getPageMargins = (top, right, bottom, left) => {
        const marginTop = `${top}in`
        const marginRight = `${right}in`
        const marginBottom = `${bottom}in`
        const marginLeft = `${left}in`
        return `@page { margin: ${marginTop} ${marginRight} ${marginBottom} ${marginLeft} !important; }`;
    };

    const createCol = (title, dataIndex, align = 'center', width = '50%') => {
        return {
            key: `${dataIndex}`,
            title,
            dataIndex,
            align,
            width
        }
    }

    const GrayHeaderBlock = (text) => {
        return (
            <Flex
                style={{ width: '100%', backgroundColor: '#E6E7E9', padding: '5px' }}
                justify='center'
                align='center'
            >
                <Text strong style={{ fontSize: '15px' }} >
                    <strong>
                        {text}
                    </strong>
                </Text>
            </Flex>
        )
    }

    const MPESection = ({ title, mpeObject }) => {
        // #D1232A Danger
        // #40AE49 Safe
        const { mpe, ae_applicable, ae_actual, excess } = mpeObject || {}
        // console.log('From Bridger', mpeObject)
        // if (!mpe || !ae_applicable || !ae_actual || !excess) throw new Error('mpeObject must have all the appropriate key-value pair and should not be null or undefined!')

        let color
        let verText

        (() => {
            const target = title.toLowerCase().includes("eye") ? "eye" : "skin"
            const part = x?.mpe?.[target]
            const excess = Number(part?.excess?.toString().replace(/,/g, ""))
            // console.log('excess', target, excess)

            if (excess > 1) {
                color = "#d82424"
                verText = `DANGEROUS FOR THE ${target.toUpperCase()}`
            }
            else if (excess <= 0.5) {
                color = "#10c96d"
                verText = `SAFE FOR THE ${target.toUpperCase()}`
            }
            else {
                color = "#ffa200"
                verText = `MARGINALLY SAFE FOR THE ${target.toUpperCase()}`
            }
        })()
        return (
            <>
                <Table
                    title={() => GrayHeaderBlock(title)}
                    columns={[
                        {
                            title: 'Maximum Permissible Emission',
                            dataIndex: 'mpe',
                            align: 'center',
                            width: '33%'
                        },
                        {
                            title: 'Accessible Emission',
                            children: [
                                {
                                    title: 'AE',
                                    dataIndex: 'ae_applicable',
                                    align: 'center'
                                },
                                {
                                    title: 'Actual AE',
                                    dataIndex: 'ae_actual',
                                    align: 'center'
                                }
                            ],
                            align: 'center',
                            width: '33%'
                        },
                        {
                            title: 'Safety Ratio',
                            dataIndex: 'excess',
                            align: 'center',
                            width: '33%'
                        }
                    ]}
                    dataSource={[
                        {
                            mpe,
                            ae_applicable,
                            ae_actual,
                            excess,
                        }
                    ]}
                    pagination={false}

                />
                <br />



                <Flex style={{ borderRadius: '5px', width: '100%', padding: '5px', backgroundColor: color }} justify='center' align='center'>
                    <Text style={{ color: 'white', fontSize: '15px' }}>
                        <strong>
                            The configuration that has been entered is {verText}!
                        </strong>
                    </Text>
                </Flex>
            </>

        )
    }

    const MPESectionRP = ({ title, mpeObject }) => {
        // #D1232A Danger
        // #40AE49 Safe
        const { mpe, ae_applicable, ae_actual, excess } = mpeObject || {}
        // console.log('From Bridger', x)
        // if (!mpe || !ae_applicable || !ae_actual || !excess) throw new Error('mpeObject must have all the appropriate key-value pair and should not be null or undefined!')
        const tableData = Object.entries(mpe).map(([key, value]) => {
            return {
                name: key,
                mpe: value.mpe,
                ae_applicable: value.ae_applicable,
                ae_actual: value.ae_actual,
                excess: value.excess,
                mostRestrictive: value.mostRestrictive

            }
        })
        let color
        let verText

        (() => {
            const target = title.toLowerCase().includes("eye") ? "eye" : "skin"
            const part = x?.mpe?.[target]
            const excess = Number(part?.excess?.toString().replace(/,/g, ""))

            if (excess > 1) {
                color = "#d82424"
                verText = `DANGEROUS FOR THE ${target.toUpperCase()}`
            }
            else if (excess <= 0.5) {
                color = "#10c96d"
                verText = `SAFE FOR THE ${target.toUpperCase()}`
            }
            else {
                color = "#ffa200"
                verText = `MARGINALLY SAFE FOR THE ${target.toUpperCase()}`
            }
        })()
        const nameHandler = (name) => {
            if (name.includes('single')) {
                return 'Single Pulse MPE'
            }
            else if (name.includes('multiple') || name.includes('pulse')) {
                return 'Pulse Train MPE'
            }
            else if (name.includes('average')) {
                return 'Average MPE'
            }
        }

        return (
            <>
                <Table
                    title={() => GrayHeaderBlock(title)}
                    columns={[
                        {
                            title: 'Maximum Permissible Emission',
                            dataIndex: 'mpe',
                            align: 'center',
                            width: '33%',
                            render: (text, record) =>
                                <>
                                    <Row justify='center'>{nameHandler(record.name)}</Row>

                                    <Row justify='center'>{record.mpe}</Row>
                                </>,
                        },
                        {
                            title: 'Accessible Emission',
                            children: [
                                {
                                    title: 'AE',
                                    dataIndex: 'ae_applicable',
                                    align: 'center'
                                },
                                {
                                    title: 'Actual AE',
                                    dataIndex: 'ae_actual',
                                    align: 'center'
                                }
                            ],
                            align: 'center',
                            width: '33%'
                        },
                        {
                            title: 'Safety Ratio',
                            dataIndex: 'excess',
                            align: 'center',
                            width: '33%'
                        }
                    ]}
                    dataSource={tableData}
                    pagination={false}
                    rowClassName={(record, index) => {
                        // Change row 0's background color
                        return record.name === x.mpe[title === "Eye" ? "eye" : "skin"].mostRestrictive ? 'highlight-row' : '';
                    }}
                />
                <br />
                <Flex style={{ borderRadius: '5px', width: '100%', padding: '5px', backgroundColor: color }} justify='center' align='center'>
                    <Text style={{ color: 'white', fontSize: '15px' }}>
                        <strong>
                            The configuration that has been entered is {verText}!
                        </strong>
                    </Text>
                </Flex>
            </>

        )
    }

    // #region
    /**
     * @typedef {Object} AELClass
     * @property {string} name - Class name (e.g., "Class 1", "Class 3B", etc.)
     * @property {string} ael - Accessible Emission Limit (e.g., "33mJ")
     * @property {string} [excess] - Excess amount over the AEL (optional)
     * @property {string} [pf] - Pass/Fail status (optional)
     * @property {boolean} [mostRestrictive] - Indicates if this is the most restrictive class (optional)
     */

    /**
     * @typedef {Object} ConditionObject
     * @property {AELClass[]} classes - Array of AEL class entries
     * @property {Object} misc - Miscellaneous additional data (currently unspecified)
     */

    /**
     * A component that renders an AEL section.
     *
     * @param {{ title: string, conditionObject: ConditionObject }} props
     */
    // #endregion
    const AELSection = ({ title, conditionObject }) => {
        const { classes, misc } = conditionObject
        const dataSourceAEL = classes.map((x) => x)
        return (
            <Card title={<Flex style={{ width: '100%' }} justify='center'>{title}</Flex>}>
                <Table
                    //#region The columns. Since it's highly unlikely someone will change this, im collapsing this for easier navigation.
                    columns={
                        [
                            {
                                title: 'Laser Class',
                                dataIndex: 'name',
                                align: "center",
                                width: '33%'
                            },
                            {
                                title: 'Accessible Emission Limit',
                                dataIndex: 'ael',
                                align: "center"

                            },
                            {
                                title: 'Emission Hazard Value',
                                dataIndex: 'excess',
                                align: 'center',
                            },
                            {
                                title: 'Pass/Fail',
                                dataIndex: 'pf',
                                align: 'center'
                            }
                        ]}
                    pagination={false}
                    // #endregion
                    dataSource={dataSourceAEL}
                />

                <Table
                    //#region The columns. Since it's highly unlikely someone will change this, im collapsing this for easier navigation.
                    style={{ width: '100%' }}
                    columns={[
                        {
                            key: 1,
                            title: `Condition 1 Misc Results`,
                            dataIndex: 'name',
                            align: 'center',
                            width: '50%'
                        },
                        {
                            key: 2,
                            title: 'Value',
                            dataIndex: 'value',
                            align: 'center'
                        }
                    ]}
                    pagination={false}
                    // #endregion
                    dataSource={misc.map(x => x)}
                />
            </Card>
        )
    }

    const AELSectionRP = ({ title, conditionObject }) => {
        const { classes, misc } = conditionObject
        const dataSourceAEL = classes.map((x) => x)

        return (
            <Card title={<Flex style={{ width: '100%' }} justify='center'>{title}</Flex>}>
                <Table
                    //#region The columns. Since it's highly unlikely someone will change this, im collapsing this for easier navigation.
                    columns={
                        [
                            {
                                title: 'Laser Class',
                                dataIndex: 'name',
                                align: "center",
                            },
                            {
                                title: 'Accessible Emission Limit',
                                align: "center",
                                children: [
                                    {
                                        key: '1',
                                        title: 'AEL Single',
                                        dataIndex: 'ael',
                                        align: 'center',
                                        render: (value, record) => (
                                            <span
                                                style={{
                                                    backgroundColor: record?.mostRestrictive?.key === 'ael' ? ' #f9f9a9' : 'transparent',

                                                }}>

                                                {value}

                                            </span>
                                        )
                                    },
                                    {
                                        key: '2',
                                        title: 'AEL s.p. Train',
                                        dataIndex: 'ael_spTrain',
                                        align: 'center',
                                        render: (value, record) => (
                                            <span
                                                style={{
                                                    backgroundColor: record?.mostRestrictive?.key === 'ael_spTrain' ? '#f9f9a9' : 'transparent',

                                                }}>

                                                {value}

                                            </span>
                                        )
                                    },
                                    {
                                        key: '3',
                                        title: 'AEL s.p.T',
                                        dataIndex: 'ael_SPT',
                                        align: 'center',
                                        render: (value, record) => (
                                            <span
                                                style={{
                                                    backgroundColor: record?.mostRestrictive?.key === 'ael_SPT' ? '#f9f9a9' : 'transparent',

                                                }}>

                                                {value}

                                            </span>
                                        )
                                    },

                                ]

                            },
                            {
                                title: 'AEL T',
                                dataIndex: 'ael_T',
                                align: "center",

                            },
                            {
                                title: 'Emission Hazard Value',
                                dataIndex: 'excess',
                                align: 'center',
                            },
                            {
                                title: 'Pass/Fail',
                                dataIndex: 'pf',
                                align: 'center',
                                render: (value, record) => (
                                    <span style={{
                                        color: record.mostRestrictive?.status === 'Pass' ? 'green' : 'red'
                                    }}>
                                        {record.mostRestrictive?.status}
                                    </span>
                                )
                            }
                        ]}
                    pagination={false}
                    // #endregion
                    dataSource={dataSourceAEL}
                />

                <Table
                    //#region The columns. Since it's highly unlikely someone will change this, im collapsing this for easier navigation.
                    style={{ width: '100%' }}
                    columns={[
                        {
                            key: 1,
                            title: `Condition 1 Misc Results`,
                            dataIndex: 'name',
                            align: 'center',
                            width: '50%'
                        },
                        {
                            key: 2,
                            title: 'Value',
                            dataIndex: 'value',
                            align: 'center'
                        }
                    ]}
                    pagination={false}
                    // #endregion
                    dataSource={misc.map(x => x)}
                />
            </Card>
        )
    }


    // #region
    /**
 * @typedef {Object} EyewearItem
 * @property {string} config - Description of the configuration (e.g., "Results at 200m")
 * @property {string} nominal_od - Nominal Optical Density
 * @property {string} rb_number - Recommended RB number
 * @property {string} lb_glass - Laser Barrier glass model
 * @property {string} lb_plastic - Laser Barrier plastic model
 */

    /**
     * A component that displays protective eyewear configurations.
     *
     * @param {{ eyewear: EyewearItem[] }} props
     */
    // #endregion
    const EyewearSection = ({ eyewear }) => {
        return (
            <Table
                // #region
                title={() => GrayHeaderBlock('Eyewear Safety')}
                columns={[
                    {
                        title: 'Configuration',
                        dataIndex: 'config',
                        width: '28%',
                        align: 'center'
                    },
                    {
                        title: 'Nominal OD',
                        dataIndex: 'nominal_od',
                        width: '18%',
                        align: 'center'
                    },
                    {
                        title: 'RB Number',
                        dataIndex: 'rb_number',
                        width: '18%',
                        align: 'center'
                    },
                    {
                        title: 'LB Number for Glass',

                        width: '18%',
                        align: 'center',
                        render: (text, record) => {
                            return <Text>{record?.lb_glass_average} {record.lb_glass}</Text>
                        }
                    },
                    {
                        title: 'LB Number for Plastic',

                        width: '18%',
                        align: 'center',
                        render: (text, record) => {
                            return <Text>{record?.lb_plastic_average} {record.lb_plastic}</Text>
                        }
                    }
                ]}
                pagination={false}
                // #endregion
                dataSource={eyewear.map(x => x)}
            />
        )
    }

    const SpectrumSection = ({ spectrum }) => {
        // console.log("spectrum", props)
        return (
            <Flex style={{ width: '100%' }} justify='center' align='center' vertical>
                {GrayHeaderBlock('Spectrum')}
                <br />
                <Flex align='center'>
                    <Space>
                        <div
                            style={{
                                width: "30px",
                                height: "30px",
                                // backgroundColor: '#00AEEF', 
                                backgroundColor: (() => {
                                    if (spectrum.range == "Infrared") {
                                        return "maroon"
                                    }
                                    else if (spectrum.range == "Ultraviolet") {
                                        return "purple"
                                    }
                                    return "rgb(" + spectrum.rgb + ")"
                                })(),
                                borderRadius: 100,
                                ustifyContent: "center"
                            }}
                        />

                        <Text>{spectrum.region}</Text>
                    </Space>
                </Flex>
            </Flex>
        )
    }

    const DisclaimerSection = () => {
        return (
            <>
                <Text><strong>Disclaimer</strong></Text>

                <Paragraph>
                    While every effort has been made to ensure the accuracy of the calculations, Lasermet cannot guarantee that results are 100%
                    accurate. For safety critical applications, users are advised to independently verify all calculations. Lasermet cannot be held
                    responsible for any inaccuracies or misuse of the information provided. Use of this software implies acceptance of these terms
                </Paragraph>
            </>
        )
    }

    const PageFooter = () => {
        return (
            <div style={{ textAlign: 'center', fontSize: '10px', marginTop: 'auto' }}>
                <Text>© {new Date().getFullYear()} Lasermet Ltd. | Confidential</Text>
            </div>
        )
    }


    const CorrectionFactor = ({ correctionFactors }) => {

        return (
            <Flex align="center" justify="center" vertical>
                <Table
                    title={() => GrayHeaderBlock('Correction Factors & Timebases')}
                    columns={[
                        {
                            title: 'Name',
                            dataIndex: 'name',
                            key: 'name',
                            align: 'center',
                            width: '50%'
                        },
                        {
                            title: 'Value',
                            dataIndex: 'pretty',
                            key: 'pretty',
                            align: 'center',

                        }
                    ]}
                    dataSource={correctionFactors?.filter(cf => cf?.val != null)}
                    rowKey="name"
                    pagination={false}
                    style={{ width: '100%' }}
                />
            </Flex>
        )
    }

    const laserOperation = ({ laserOperation }) => {
        if (laserOperation.toLowerCase().includes('cw')) {
            if (laserOperation.includes('FO')) {
                return 'Fiber Optic Continuous Wave'
            }
            return 'Continuous Wave'
        }
        else if (laserOperation.includes('singlepulse')) {
            if (laserOperation.includes('FO')) {
                return 'Fiber Optic Single Pulse'
            }
            return 'Single Pulse'
        }
        else if (laserOperation.includes('epetitivelypulsed')) {
            if (laserOperation.includes('FO')) {
                return 'Repetitively Pulsed'
            }
            return 'Repetitively Pulsed'
        }
    }

    const sourceType = ({ sourceType }) => {
        // console.log('sourcing', sourceType)
        if (sourceType.includes('singlemode')) {
            return 'Single Mode '
        }
        else if (sourceType.includes('multimodeSI')) {
            return 'Multimode-Step Index'
        }
        else if (sourceType.includes('multimodeGI')) {
            return 'Multimode-Graded Index'
        }
        else if (sourceType.includes('Diffuse')) {
            return 'Diffuse Reflective'
        }
        return sourceType.includes('point') ? 'Point Source' : 'Extended Source'
    }

    //#endregion

    return (
        <>
            <ConfigProvider theme={customTheme}>
                <div
                    className='printable-container'
                    ref={ref}
                >
                    <style>{getPageMargins(0.5, 0.5, 0.5, 0.5)}</style>

                    {/* Page 1 */}
                    <div className='page-break a4-page'>
                        <div style={{ flexGrow: 1 }}>
                            <Flex style={{ width: '100%' }} align='flex-start' justify='space-between'>
                                <img style={{ height: '100px', display: 'block' }} src={Logo} />   
                                <Text>Date: {dateNow} </Text>
                            </Flex>

                            <br />

                            <Flex vertical style={{ width: '100%' }} justify='center' align='center'>
                                <Title level={4}>{sourceType(x)} {laserOperation(x)} Results for {x.standard}</Title>
                                <Title level={4}>{props.laserName}</Title>
                            </Flex>

                            <br />

                            <Text>{"A) Laser Properties"}</Text>

                            <br />

                            <Table
                                title={() => GrayHeaderBlock(`${laserOperation(x)} ${sourceType(x)}`)}
                                columns={[
                                    createCol('Laser Operation', 'laserOperation'),
                                    createCol('Source', 'source')
                                ]}
                                dataSource={[
                                    {
                                        laserOperation: laserOperation(x),
                                        source: sourceType(x)
                                    }
                                ]}
                                pagination={false}
                            />

                            <br />
                            <Table
                                title={() => GrayHeaderBlock(`${laserOperation(x)} Settings`)}
                                columns={[
                                    createCol('Settings', 'x'),
                                    createCol('Value', 'y')
                                ]}
                                dataSource={[
                                    { x: 'Wavelength', y: x.wavelength },
                                    // {
                                    //     x: x.laserOperation !== "singlePulse" ? 'Exposure Duration: ' : 'Pulse Duration: ',
                                    //     y: x.laserOperation !== "singlePulse" ? x.time : x.pulseDuration
                                    // },
                                    ...(x.laserOperation.toLowerCase().includes("cw")
                                        ? [{ x: "Exposure Duration", y: x.time },
                                        ]
                                        : x.laserOperation.toLowerCase().includes("repetitivelypulsed")
                                            ? [
                                                { x: "Exposure Duration", y: x.time },
                                                ...(x.pulseDuration ? [{ x: "Pulse Duration", y: x.pulseDuration }]
                                                    : [
                                                        { x: "Pulse Duration", y: x.pulseDuration },
                                                    ]),

                                                { x: "Pulse Frequency", y: x.pulseFrequency }
                                            ]
                                            : [
                                                { x: "Pulse Duration", y: x.pulseDuration }
                                            ]
                                    ),

                                    ...(x.power ?
                                        [
                                            { x: 'Laser Power', y: x.power }
                                        ] : x.peakPower ? [
                                            { x: 'Peak Power', y: x.peakPower }
                                        ] : x.averagePower ? [
                                            { x: 'Average Power', y: x.averagePower }
                                        ]
                                            : [
                                                { x: 'Energy', y: x.energy }
                                            ]),
                                    ...(x.apparentSourceSize ?
                                        [
                                            { x: 'Apparent Source Size', y: x.apparentSourceSize }
                                        ] : x.diameter ?
                                            [
                                                { x: 'Fiber Core Diameter', y: x.diameter }
                                            ]
                                            : x.numerical_aperture ?
                                                [
                                                    { x: 'Numerical Aperture', y: x.numerical_aperture }
                                                ]
                                                : [
                                                    { x: 'Beam Diameter', y: x.beamDiameter }
                                                ]
                                    ),
                                    ...(x.IGF ?
                                        [
                                            { x: 'Index Grading Factor', y: x.IGF }
                                        ] : []),
                                    ...(x.laserOperation.includes('FO') ? [] : [
                                        { x: 'Beam Divergence', y: x.beamDivergence }
                                    ]),

                                    ...(x?.distance2 ?
                                        [
                                            { x: 'Laser to Surface Distance', y: x.distance },
                                            { x: 'Surface to Eye Distance', y: x?.distance2 },
                                            { x: 'Albedo', y: x?.albedo }]

                                        : [{ x: 'Distance to target', y: x.distance },])
                                ]}
                                pagination={false}
                            />
                            <br />
                        </div>
                        <PageFooter />
                    </div>

                    {/* Page 2 */}
                    <div className='page-break a4-page' >
                        <div style={{ flexGrow: 1 }}>
                            <Text>{"B) Calculation Results"}</Text>
                            {props.result.laserOperation.includes('epetitively') ?
                                <>
                                    <MPESectionRP title='Eye' mpeObject={x.mpe.eye} />
                                    <br />
                                    <MPESectionRP title='Skin' mpeObject={x.mpe.skin} />
                                    <br />
                                </>
                                :
                                <>
                                    <MPESection title='Eye' mpeObject={x.mpe.eye} />
                                    <br />
                                    <MPESection title='Skin' mpeObject={x.mpe.skin} />
                                    <br />
                                    {/* Beam Properties */}
                                    <Table
                                        title={() => GrayHeaderBlock('Beam Properties')}
                                        columns={[
                                            createCol('NOHD', 'nohd', 'center', '10%'),
                                            createCol('ENOHD', 'enohd', 'center', '10%'),
                                            createCol('Spot Size', 'spot_size', 'center', '10%'),
                                            {
                                                title: 'Limit Aperture',
                                                children: [
                                                    createCol('Eye', 'eye_LA', 'center', '17.5%'),
                                                    createCol('Skin', 'skin_LA', 'center', '17.5%')
                                                ],
                                                align: 'center',

                                            },
                                            {
                                                title: 'Applicable Beam Area',
                                                children: [
                                                    createCol('Eye', 'applicable_eye', 'center', '17.5%'),
                                                    createCol('Skin', 'applicable_skin', 'center', '17.5%')
                                                ],
                                                align: 'center',

                                            }
                                        ]}
                                        dataSource={[
                                            {
                                                nohd: x.nohd,
                                                enohd: x.enohd,
                                                spot_size: x.spot_size,
                                                eye_LA: x.eye_LA,
                                                skin_LA: x.skin_LA,
                                                applicable_eye: x.applicable_eye,
                                                applicable_skin: x.applicable_skin,
                                            }
                                        ]}
                                        pagination={false}
                                    />
                                    <SpectrumSection spectrum={x.spectrum} />
                                </>
                            }

                        </div>
                        <PageFooter />
                    </div>

                    {/*Page 2 extension*/}
                    {props.result.laserOperation.includes('epetitively') &&
                        <div className='page-break a4-page' >
                            <div style={{ flexGrow: 1 }}>
                                <Table
                                    title={() => GrayHeaderBlock('Beam Properties')}
                                    columns={[
                                        createCol('NOHD', 'nohd', 'center', '0%'),
                                        createCol('ENOHD', 'enohd', 'center', '0%'),
                                        createCol('Spot Size', 'spot_size', 'center', '0%'),
                                        {
                                            title: 'Limit Aperture',
                                            children: [
                                                createCol('Eye', 'eye_LA', 'center', '0%'),
                                                createCol('Skin', 'skin_LA', 'center', '0%')
                                            ],
                                            align: 'center',

                                        },
                                        {
                                            title: 'Applicable Beam Area',
                                            children: [
                                                createCol('Eye', 'applicable_eye', 'center', '0%'),
                                                createCol('Skin', 'applicable_skin', 'center', '0%')
                                            ],
                                            align: 'center',

                                        }
                                    ]}
                                    dataSource={[
                                        {
                                            nohd: x.nohd,
                                            enohd: x.enohd,
                                            spot_size: x.spot_size,
                                            eye_LA: x.eye_LA,
                                            skin_LA: x.skin_LA,
                                            applicable_eye: x.applicable_eye,
                                            applicable_skin: x.applicable_skin,
                                        }
                                    ]}
                                    pagination={false}
                                />
                                <SpectrumSection spectrum={x.spectrum} />
                            </div>
                            <PageFooter />
                        </div>
                    }

                    {/* Page 3 */}
                    <div className='page-break a4-page' >
                        <div style={{ flexGrow: 1 }}>
                            <Text>{'C) Correction Factors & Timebases'}</Text>
                            <br />
                            <CorrectionFactor correctionFactors={x.correctionFactors} />
                        </div>
                        <PageFooter />
                    </div>

                    {/* Page 4 */}
                    <div className='page-break a4-page' >
                        <div style={{ flexGrow: 1 }}>
                            <Text>{'D) Classification Details'}</Text>
                            <br />
                            <Flex justify='center' align='center'>
                                <Text>These Accessible Emission Limit (AEL) has a computation time base of {x.timebase}.</Text>
                            </Flex>
                            <br />
                            <Text>{`D.1) Condition 1`}</Text>
                            {props.result.laserOperation.includes('epetitively') ? (
                                <AELSectionRP title='Condition 1' conditionObject={x.ael.condition1} />
                            ) : <AELSection title='Condition 1' conditionObject={x.ael.condition1} />


                            }
                        </div>
                        <PageFooter />
                    </div>
                    {props.result.laserOperation.includes('FO') &&
                        <div className='page-break a4-page' >
                            <div style={{ flexGrow: 1 }}>
                                <Text>{'D) Classification Details'}</Text>
                                <br />
                                <Flex justify='center' align='center'>
                                    <Text>These Accessible Emission Limit (AEL) has a computation time base of {x.timebase}.</Text>
                                </Flex>
                                <br />
                                <Text>{`D.1) Condition 2`}</Text>
                                {props.result.laserOperation.includes('epetitively') ? (
                                    <AELSectionRP title='Condition 2' conditionObject={x.ael.condition2} />
                                ) : <AELSection title='Condition 2' conditionObject={x.ael.condition2} />


                                }
                            </div>
                            <PageFooter />
                        </div>
                    }

                    {/* Page 5 */}
                    <div className='page-break a4-page'>
                        <div style={{ flexGrow: 1 }}>
                            <Flex style={{ width: '100%' }} justify='center' align='center'>
                                <Paragraph>These Accessible Emission Limit (AEL) has a computation time base of 100 seconds.</Paragraph>
                            </Flex>
                            <Text>{`D.2) Condition 3`}</Text>
                            {props.result.laserOperation.includes('epetitively') ? (
                                <AELSectionRP title='Condition 3' conditionObject={x.ael.condition3} />
                            ) : <AELSection title='Condition 3' conditionObject={x.ael.condition3} />
                            }
                            <br />
                            <Text>{`E) Laser Classification`}</Text>
                            <br />
                            <Flex style={{ width: '100%' }} justify='center' align='center' vertical>
                                {/* <Text>This laser configuration is classified as</Text>
                                <Text style={{ fontSize: '48px' }}><strong>{x.verdict}</strong></Text> */}
                                <div style={{ disaply: "flex", justifySelf: "center", alignSelf: "center", height: "100%", padding: "5px", border: "4px solid #1d1f8c", borderRadius: "15px", display: 'flex', textAlign: "center", justifyContent: "center", alignItems: 'center' }}>
                                    <strong style={{ fontSize: '20px', color: "#1d1f8c" }}><span>THIS LASER CONFIGURATION IS CLASSIFIED AS A </span>CLASS {x.verdict} LASER</strong>
                                </div>
                            </Flex>

                        </div>
                        <PageFooter />
                    </div>

                    {/* Page 6 */}
                    <div className='page-break a4-page'>
                        <div style={{ flexGrow: 1 }}>
                            <Flex vertical justify='space-between' style={{ height: '100%' }}>
                                <Flex vertical>

                                    <Paragraph>{'F) Safety Eyewear and Filters'}</Paragraph>

                                    <EyewearSection eyewear={x.eyewear} />
                                </Flex>

                                {/* I want the div above to occupy the upper space. while everything below should be rendered at the footer part. */}

                                <Flex vertical>
                                    <DisclaimerSection />
                                </Flex>
                            </Flex>
                        </div>
                        <PageFooter />
                    </div>
                </div>
            </ConfigProvider >
        </>
    )
})

export default Printable
